// ============================================================================
// mapStyle.ts — построение MapLibre-стиля из источника тайлов (SSoT/SRP).
//
//   style   → готовый стиль по URL (отдаётся как есть);
//   raster  → raster-слой по шаблону {z}/{x}/{y};
//   pmtiles → vector-слой из PMTiles-архива (архив = один файл).
//
// Стиль БЕЗ symbol-слоёв: подписи требуют fonts/glyphs (внешний ресурс),
// поэтому базовый офлайн-рендер — земля/вода/застройка/дороги/границы.
// ============================================================================

import type { StyleSpecification } from 'maplibre-gl';
import { resolvePmtilesSourceUrl } from './pmtilesProtocol';
import type { GeoTileSource } from './mapConfig';

/** Базовый vector-стиль под Protomaps-совместимые PMTiles (source-layer имена). */
function pmtilesVectorStyle(tileUrl: string, attribution: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      base: {
        type: 'vector',
        url: tileUrl,
        attribution,
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#eef0f2' } },
      { id: 'land', type: 'fill', source: 'base', 'source-layer': 'earth', paint: { 'fill-color': '#f4f2ec' } },
      { id: 'landuse', type: 'fill', source: 'base', 'source-layer': 'landuse', paint: { 'fill-color': '#e7e8dc', 'fill-opacity': 0.7 } },
      { id: 'water', type: 'fill', source: 'base', 'source-layer': 'water', paint: { 'fill-color': '#a9c9e4' } },
      { id: 'buildings', type: 'fill', source: 'base', 'source-layer': 'buildings', paint: { 'fill-color': '#dcd9d2', 'fill-opacity': 0.8 } },
      {
        id: 'roads-casing', type: 'line', source: 'base', 'source-layer': 'roads',
        paint: { 'line-color': '#d3cfc7', 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 12, 2.4, 18, 7] },
      },
      {
        id: 'roads', type: 'line', source: 'base', 'source-layer': 'roads',
        paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.4, 12, 1.6, 18, 5] },
      },
      {
        id: 'boundaries', type: 'line', source: 'base', 'source-layer': 'boundaries',
        paint: { 'line-color': '#9aa0a6', 'line-width': 0.8, 'line-dasharray': [2, 1.5] },
      },
    ],
  };
}

function rasterStyle(url: string, attribution: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      raster: {
        type: 'raster',
        tiles: [url],
        tileSize: 256,
        attribution,
      },
    },
    layers: [{ id: 'raster', type: 'raster', source: 'raster' }],
  };
}

/**
 * Стиль карты для MapLibre. Строка — URL готового стиля; объект — стиль,
 * построенный из PMTiles/растровых тайлов.
 */
export function buildMapStyle(ts: GeoTileSource): StyleSpecification | string {
  if (ts.kind === 'style') return ts.url;
  if (ts.kind === 'pmtiles') return pmtilesVectorStyle(resolvePmtilesSourceUrl(ts.url), ts.attribution);
  return rasterStyle(ts.url, ts.attribution);
}
